import { useMutation, useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { schemas, ZInfer } from '@showrunner/scrapi'

import { IOrg, useMst } from '#state'

import {
  BillingPlan,
  canUpgradeOrg,
  PaidFeature,
  PaidNote,
  paidNote,
  StaffBillingPlan,
  STRIPE_TIER,
} from './plans'

type BillingErrorCode = ZInfer<typeof schemas.BillingErrorCode>

export const billingErrorMessage = (code: BillingErrorCode): string => {
  switch (code) {
    case 'ACCOUNT_CONFLICT':
      return 'This workspace is billed through a different account'
    case 'BILLING_DISABLED':
      return 'Billing is not available right now'
    case 'INVALID_CHECKOUT_SESSION':
      return 'That checkout session is no longer valid'
    case 'INVALID_PROMOTION_CODE':
      return 'That code is not valid'
    case 'NO_SUBSCRIPTION':
      return 'This workspace has no subscription to manage'
    case 'NOT_NEW_SUBSCRIPTION':
      return 'There is already a subscription on this account'
    case 'PAYMENT_FAILED':
      return "Your payment didn't go through. Complete it on the invoice that opened in a new tab within 23 hours to add the seat."
    case 'PLAN_SEAT_MAX_REACHED':
      return 'This plan has no seats left'
    case 'SEAT_CHANGE_NOT_ALLOWED':
      return 'Your plan does not allow this seat change'
    case 'SEAT_REDUCTION_BELOW_USAGE':
      return 'Members and open invitations use more seats than that'
    case 'STRIPE_UNAVAILABLE':
      return 'Our payment provider is unreachable, try again shortly'
  }
}

export const billingErrorSchema = z.object({
  status: z.literal(420),
  body: schemas.BillingErrorResponse[420],
})

export const billingFailure = (
  err: unknown,
  fallback: string,
): { code?: BillingErrorCode; message: string } => {
  const known = billingErrorSchema.safeParse(err)
  if (!known.success) return { message: fallback }

  const { code } = known.data.body
  return { code, message: billingErrorMessage(code) }
}

const asPlan = (body: unknown): BillingPlan | null => {
  const parsed = schemas.GetBillingPlanResponse.safeParse(body)
  return parsed.success ? parsed.data.plan : null
}

const asStaffPlan = (body: unknown): StaffBillingPlan | null => {
  const parsed = schemas.GetStaffBillingPlanResponse.safeParse(body)
  return parsed.success ? parsed.data.plan : null
}

export const billingPlanQueryKey = (workspaceId: string) => [
  'billing.getPlan',
  workspaceId,
]

export const useWorkspaceBillingPlan = (
  workspaceId: string,
  { enabled = true, staleTime }: { enabled?: boolean; staleTime?: number } = {},
) => {
  const { scrapi } = useMst()

  return useQuery<BillingPlan | null>({
    enabled,
    staleTime,
    queryKey: billingPlanQueryKey(workspaceId),
    queryFn: async () => {
      const result = await scrapi.billing.getPlan()
      if (result.status === 200) {
        return asPlan(result.body)
      }
      throw new Error('Unexpected result', { cause: result })
    },
  })
}

export const useOrgBillingPlan = (org?: IOrg) =>
  useWorkspaceBillingPlan(org?.id ?? '', {
    enabled: org?.tier === STRIPE_TIER,
  })

export const useCanUpgrade = (org: IOrg | undefined) => {
  const { user } = useMst()
  return canUpgradeOrg(org, user.id)
}

export const usePaidNote = (
  org: IOrg | undefined,
  features: PaidFeature[],
): PaidNote | null => {
  if (!org) return null

  return paidNote(features, { isUnpaid: org.isUnpaid, tier: org.tier })
}

export const useStaffWorkspaceBillingPlan = (
  workspaceId: string,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const { scrapi } = useMst()

  return useQuery<StaffBillingPlan | null>({
    enabled,
    queryKey: ['staff.workspaceBillingPlan', workspaceId],
    queryFn: async () => {
      const result = await scrapi.staff.workspaceBillingPlan({
        params: { id: workspaceId },
      })
      if (result.status === 200) {
        return asStaffPlan(result.body)
      }
      throw new Error('Unexpected result', { cause: result })
    },
  })
}

export const usePricingTableSession = () => {
  const { scrapi } = useMst()

  const session = useMutation({
    async mutationFn() {
      const result = await scrapi.billing.createPricingTableSession({
        body: {},
      })
      if (result.status !== 200) throw result
      return result.body
    },
  })

  return { pending: session.isPending, createSession: session.mutateAsync }
}

export const usePortal = () => {
  const { scrapi } = useMst()

  const portalMutation = useMutation({
    async mutationFn(returnPath: string) {
      const result = await scrapi.billing.createPortalSession({
        body: { returnPath },
      })
      if (result.status !== 200) throw result
      return result.body
    },
  })

  const openPortal = async (
    returnPath: string = window.location.pathname,
  ): Promise<{
    success: boolean
    code?: BillingErrorCode
    message?: string
  }> => {
    try {
      const { url } = await portalMutation.mutateAsync(returnPath)
      window.location.assign(url)
      return { success: true }
    } catch (err) {
      return {
        success: false,
        ...billingFailure(err, 'Failed to open billing'),
      }
    }
  }

  return {
    pending: portalMutation.isPending || portalMutation.isSuccess,
    openPortal,
  }
}
